import LoadPanel from 'app/components/LoadPanel';
import { NotFoundPage } from 'app/components/NotFoundPage';
import { WrapContent } from 'app/components/WrapContent';
import useAxios from 'axios-hooks';
import useDictionary from 'hooks/useDictionary';
import { groupBy } from 'lodash';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { notification } from 'utils/notification';

const List = React.lazy(() => import('./List'));
const InstanceUser = React.lazy(() => import('./InstanceUser'));
const ChangeSubscribe = React.lazy(() => import('./ChangeSubscribe'));
const PaymentAgain = React.lazy(() => import('./PaymentAgain'));
const PayForPaymentAgain = React.lazy(() => import('./PayForPaymentAgain'));
const PayForChangeSubscribe = React.lazy(
  () => import('./PayForChangeSubscribe'),
);

/**
 * create context
 */
const ModuleContext = createContext({});
ModuleContext.displayName = 'UserPaymentManager_Context';
export const useModuleContext = () => {
  return useContext(ModuleContext);
};

const UserPaymentManager = () => {
  const { t }: any = useDictionary({ programId: 'user-payment' });
  const [data, setData] = useState<Array<any>>([]);
  const [modeView, setModeView] = useState<any>({
    type: 'list',
    data: null,
  });

  const [{ loading }, refetchData] = useAxios(
    {},
    { manual: true, autoCancel: true, useCache: false },
  );

  useEffect(() => {
    onSubmit();
  }, []);

  useEffect(() => {
    if (modeView?.type !== 'list' || !modeView?.newInstanceUser) return;
    const { serviceId, instanceId, instanceUsers } = modeView?.newInstanceUser;
    data.forEach(o => {
      if (o.serviceId == serviceId) {
        const instance = o.instances?.find(
          ins => ins?.instanceId == instanceId,
        );
        if (instance) {
          instance.instanceUsers = [...instanceUsers];
        }
      }
    });
    setData([...data]);
  }, [modeView]);

  /**
   * on submit
   *
   */
  const onSubmit = async () => {
    const res = await refetchData({
      url: '/user-payment/payment-and-instance-user',
      method: 'GET',
    });
    if (res?.data?.status === '200') {
      const { instances, instanceUsers = [] } = res?.data?.data || {};
      const serviceGroup = groupBy(instances || [], 'serviceId');
      const dataMapping: Array<any> = [];
      Object.entries(serviceGroup).forEach(([key, value]) => {
        const instancesService: any = value;
        instancesService.forEach(ins => {
          ins.instanceUsers = instanceUsers.filter(
            o =>
              o.userId === ins.userId &&
              o.serviceId === ins.serviceId &&
              o.instanceId === ins.instanceId,
          );
        });
        dataMapping.push({
          serviceId: key,
          serviceNm: instancesService?.[0]?.serviceNm,
          instances: instancesService,
        });
      });
      setData(dataMapping);
    } else {
      notification({ res });
    }
  };

  const value: any = {
    t,
    data,
    setData,
    modeView,
    setModeView,
    onSubmit,
  };

  /**
   * render content
   *
   * @return {*}
   */
  const renderContent = () => {
    switch (modeView?.type) {
      case 'list':
        return <List />;
      case 'change-subscribe':
        return <ChangeSubscribe />;
      case 'pay-for-change-subscribe':
        return <PayForChangeSubscribe />;
      case 'payment-again':
        return <PaymentAgain />;
      case 'pay-for-payment-again':
        return <PayForPaymentAgain />;
      case 'user':
        return <InstanceUser />;
      default:
        return <NotFoundPage />;
    }
  };

  return (
    <WrapContent>
      <ModuleContext.Provider value={value}>
        <React.Suspense fallback={<LoadPanel visible={true} />}>
          <LoadPanel visible={loading} />
          {renderContent()}
        </React.Suspense>
      </ModuleContext.Provider>
    </WrapContent>
  );
};

export default UserPaymentManager;
